import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import { getLoginRequiredNotification } from '../../utils/notificationHelpers';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title);

const BudgetAllocator = () => {
  const [totalBudget, setTotalBudget] = useState('');
  const [priorities, setPriorities] = useState({});
  const [categories, setCategories] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [savedBudgetId, setSavedBudgetId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Luxury wedding category data with icons and colors
  const categoryData = {
    'Venue': { 
      icon: '🏛️', 
      color: 'from-rose-200 to-pink-300',
      bgGradient: 'from-rose-50 to-pink-100',
      chartColor: '#F8BBD9'
    },
    'Catering': { 
      icon: '🍽️', 
      color: 'from-champagne-200 to-gold-300',
      bgGradient: 'from-champagne-50 to-gold-100',
      chartColor: '#F7E7CE'
    },
    'Photography': { 
      icon: '📸', 
      color: 'from-purple-200 to-lavender-300',
      bgGradient: 'from-purple-50 to-lavender-100',
      chartColor: '#E6D7FF'
    },
    'Flowers': { 
      icon: '🌸', 
      color: 'from-pink-200 to-rose-300',
      bgGradient: 'from-pink-50 to-rose-100',
      chartColor: '#FFB6C1'
    },
    'Music': { 
      icon: '🎵', 
      color: 'from-blue-200 to-indigo-300',
      bgGradient: 'from-blue-50 to-indigo-100',
      chartColor: '#B6D7FF'
    },
    'Attire': { 
      icon: '👗', 
      color: 'from-mauve-200 to-purple-300',
      bgGradient: 'from-mauve-50 to-purple-100',
      chartColor: '#D8BFD8'
    },
    'Transportation': { 
      icon: '🚗', 
      color: 'from-teal-200 to-cyan-300',
      bgGradient: 'from-teal-50 to-cyan-100',
      chartColor: '#B0E0E6'
    },
    'Invitations': { 
      icon: '💌', 
      color: 'from-blush-200 to-pink-300',
      bgGradient: 'from-blush-50 to-pink-100',
      chartColor: '#FFCCCB'
    },
    'Rings': { 
      icon: '💍', 
      color: 'from-gold-200 to-yellow-300',
      bgGradient: 'from-gold-50 to-yellow-100',
      chartColor: '#FFD700'
    },
    'Miscellaneous': { 
      icon: '✨', 
      color: 'from-emerald-200 to-mint-300',
      bgGradient: 'from-emerald-50 to-mint-100',
      chartColor: '#98FB98'
    }
  };

  useEffect(() => {
    loadCategories();
    loadSavedBudget();
    setIsVisible(true);
  }, []);

  const loadCategories = async () => {
    try {
      const response = await apiService.budget.getCategories();
      const categoriesData = response.data.categories || response.data;
      setCategories(categoriesData);
      
      const defaultPriorities = {};
      categoriesData.forEach(category => {
        defaultPriorities[category.category] = 'medium';
      });
      setPriorities(defaultPriorities);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load budget categories');
    }
  };

  const handlePriorityChange = (categoryName, priority) => {
    setPriorities({
      ...priorities,
      [categoryName]: priority
    });
  };

  const calculateBudget = async () => {
    if (!totalBudget || totalBudget <= 0) {
      setError('Please enter a valid total budget');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const priorityWeights = {};
      Object.keys(priorities).forEach(categoryName => {
        const priority = priorities[categoryName];
        let weight = 1.0;
        if (priority === 'low') weight = 0.7;
        else if (priority === 'high') weight = 1.3;
        priorityWeights[categoryName.toLowerCase()] = weight;
      });

      const response = await apiService.budget.allocate({
        total_budget: parseFloat(totalBudget),
        priorities: priorityWeights
      });
      
      const allocationsData = response.data.allocations || response.data;
      setAllocations(allocationsData);
      setHasUnsavedChanges(true); // Mark as having unsaved changes after calculation
    } catch (error) {
      console.error('Error calculating budget:', error);
      setError(error.response?.data?.error || 'Failed to calculate budget allocation');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTotalBudget('');
    setAllocations([]);
    setError(null);
    setSavedBudgetId(null);
    setHasUnsavedChanges(false);
    const defaultPriorities = {};
    categories.forEach(category => {
      defaultPriorities[category.category] = 'medium';
    });
    setPriorities(defaultPriorities);
  };

  const loadSavedBudget = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    
    // Only load saved budget on initial page load, not when user is actively working
    if (!isInitialLoad) return;
    
    try {
      const response = await apiService.budget.getSavedBudget(userId);
      if (response.data.budget) {
        const { total_budget, allocations, priorities, id } = response.data.budget;
        setTotalBudget(total_budget);
        setAllocations(allocations);
        setPriorities(priorities || {});
        setSavedBudgetId(id);
        setHasUnsavedChanges(false); // Loaded budget is already saved
      }
    } catch (error) {
      console.log('No saved budget found');
    } finally {
      setIsInitialLoad(false); // Mark initial load as complete
    }
  };

  const saveBudget = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!token || !userId) {
      setNotification(getLoginRequiredNotification());
      return;
    }
    
    // Check if budget has been calculated
    if (!allocations.length) {
      setNotification({
        show: true,
        title: 'No Budget to Save',
        message: 'Please calculate your budget allocation first before saving.',
        type: 'warning'
      });
      return;
    }
    
    // Check if there are unsaved changes (must recalculate after changing budget)
    if (!hasUnsavedChanges) {
      setNotification({
        show: true,
        title: 'Recalculation Required',
        message: 'Please recalculate your budget before saving. Click "Calculate Budget" to update allocations.',
        type: 'warning'
      });
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      const response = await apiService.budget.saveBudget({
        user_id: parseInt(userId),
        total_budget: parseFloat(totalBudget),
        allocations: allocations,
        priorities: priorities
      });
      
      setSavedBudgetId(response.data.budget.id);
      setHasUnsavedChanges(false); // Reset flag after successful save
      
      // Show success notification
      setNotification({
        show: true,
        title: 'Budget Saved Successfully!',
        message: '💰 Your wedding budget has been saved! Visit your dashboard to view it anytime.',
        type: 'success'
      });
      
    } catch (error) {
      console.error('Error saving budget:', error);
      setNotification({
        show: true,
        title: 'Save Failed',
        message: error.response?.data?.error || 'Failed to save budget. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'from-red-400 to-rose-500';
      case 'medium': return 'from-blue-400 to-indigo-500';
      case 'low': return 'from-green-400 to-emerald-500';
      default: return 'from-blue-400 to-indigo-500';
    }
  };

  // Enhanced 3D chart data
  const chartData = {
    labels: allocations.map(item => item.category),
    datasets: [
      {
        data: allocations.map(item => item.amount),
        backgroundColor: allocations.map(item => 
          categoryData[item.category]?.chartColor || '#E6E6FA'
        ),
        borderColor: '#ffffff',
        borderWidth: 3,
        hoverBorderWidth: 5,
        hoverOffset: 15,
        shadowOffsetX: 3,
        shadowOffsetY: 3,
        shadowBlur: 10,
        shadowColor: 'rgba(0, 0, 0, 0.3)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#374151',
        borderColor: '#d1d5db',
        borderWidth: 2,
        cornerRadius: 12,
        padding: 16,
        titleFont: { family: 'serif', size: 16, weight: 'bold' },
        bodyFont: { family: 'sans-serif', size: 14 },
        callbacks: {
          label: function(context) {
            const percentage = ((context.parsed / parseFloat(totalBudget)) * 100).toFixed(1);
            return `${context.label}: $${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutCubic'
    },
    onHover: (event, activeElements) => {
      if (activeElements.length > 0) {
        const index = activeElements[0].index;
        setHoveredCategory(allocations[index]?.category);
      } else {
        setHoveredCategory(null);
      }
    }
  };

  const budgetIcons = [
    { emoji: '💰', size: '4xl', opacity: '15' },
    { emoji: '💎', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '🎯', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={budgetIcons} />
      
      {/* Animated Background Sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-3xl animate-pulse">💰</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Budget Allocator
          </h1>
          
          {/* Gold accent underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Create your perfect wedding budget with AI-powered insights and elegant planning tools
          </p>
        </div>

        <div className="grid xl:grid-cols-3 gap-8">
          
          {/* Input Section */}
          <div className="xl:col-span-1 space-y-6">
            
            {/* Budget Input Card */}
            <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-gold-400 to-yellow-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl">💎</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent">
                    Total Budget
                  </h3>
                </div>
                
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl font-bold text-gold-500">$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={totalBudget}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setTotalBudget(value);
                    }}
                    placeholder="Enter your dream budget"
                    className="w-full pl-12 pr-6 py-4 text-2xl font-bold border-2 border-gold-200 rounded-2xl focus:ring-4 focus:ring-gold-300 focus:border-gold-400 transition-all duration-300 bg-gradient-to-r from-white to-gold-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              </div>
            </div>

            {/* Categories Card */}
            <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                    <span className="text-2xl">⚖️</span>
                  </div>
                  <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Category Priorities
                  </h3>
                </div>
                
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  {categories.map((category, index) => (
                    <div 
                      key={category.category}
                      className={`group relative bg-gradient-to-r ${categoryData[category.category]?.bgGradient || 'from-gray-50 to-gray-100'} rounded-2xl p-4 border border-white/50 hover:shadow-xl transition-all duration-500 hover:scale-105`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="text-2xl flex-shrink-0">
                            {categoryData[category.category]?.icon || '✨'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-800 text-base truncate">{category.category}</h4>
                            <p className="text-xs text-gray-600 font-medium line-clamp-2 break-words">{category.description}</p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-2 flex-shrink-0">
                          <select
                            value={priorities[category.category] || 'medium'}
                            onChange={(e) => handlePriorityChange(category.category, e.target.value)}
                            className="px-3 py-1.5 border-2 border-white/50 rounded-lg focus:ring-2 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-white/90 font-bold text-xs"
                          >
                            <option value="low">🟢 Low</option>
                            <option value="medium">🟡 Medium</option>
                            <option value="high">🔴 High</option>
                          </select>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${getPriorityColor(priorities[category.category])} shadow-lg`}>
                            {priorities[category.category]?.toUpperCase() || 'MEDIUM'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`space-y-4 transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="flex gap-4">
                <button
                  onClick={calculateBudget}
                  disabled={loading || !totalBudget}
                  className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white py-4 px-8 rounded-2xl font-bold text-lg animate-pulse hover:animate-none hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      ✨ Calculating Magic...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2 text-xl">🎯</span>
                      {allocations.length > 0 ? 'Recalculate Budget' : 'Calculate Budget'}
                    </div>
                  )}
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-yellow-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </button>
                
                <button
                  onClick={resetForm}
                  className="px-8 py-4 border-2 border-rose-300 text-rose-600 rounded-2xl font-bold text-lg hover:bg-rose-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  🔄 Reset
                </button>
              </div>

              {/* Save Budget Button - Full Width */}
              {allocations.length > 0 && (
                <button
                  onClick={saveBudget}
                  disabled={isSaving || !hasUnsavedChanges}
                  className="w-full relative overflow-hidden bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white py-4 px-8 rounded-2xl font-bold text-lg animate-pulse hover:animate-none hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSaving ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      💾 Saving...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <span className="mr-2">💾</span>
                      {savedBudgetId ? 'Update Saved Budget' : 'Save Budget'}
                    </div>
                  )}
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-yellow-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </button>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg animate-shake">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⚠️</span>
                  <span className="font-bold">{error}</span>
                </div>
              </div>
            )}
          </div>

          {/* Results Section */}
          <div className="xl:col-span-2">
            {allocations.length > 0 && (
              <div className="space-y-8">
                
                {/* 3D Interactive Chart */}
                <div className={`transition-all duration-700 delay-800 ${allocations.length > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
                    <div className="text-center mb-8">
                      <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-4">
                        🎨 Your Perfect Budget Distribution
                      </h3>
                      <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-pink-500 mx-auto rounded-full"></div>
                    </div>
                    
                    <div className="relative h-96">
                      <Doughnut data={chartData} options={chartOptions} />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center bg-white/90 rounded-full p-6 shadow-xl border border-gold-200">
                          <div className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                            ${parseFloat(totalBudget).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-600 font-semibold">Total Budget</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Animated Category Tiles */}
                <div className={`transition-all duration-700 delay-1000 ${allocations.length > 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-serif font-bold bg-gradient-to-r from-gold-600 to-yellow-600 bg-clip-text text-transparent mb-4">
                      💫 Detailed Budget Breakdown
                    </h3>
                    <div className="w-24 h-1 bg-gradient-to-r from-gold-400 to-yellow-500 mx-auto rounded-full"></div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {allocations.map((allocation, index) => (
                      <div
                        key={index}
                        className={`group relative bg-gradient-to-br ${categoryData[allocation.category]?.bgGradient || 'from-gray-50 to-gray-100'} rounded-2xl p-6 border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 animate-fadeInScale`}
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="text-4xl animate-bounce">
                              {categoryData[allocation.category]?.icon || '✨'}
                            </div>
                            <div>
                              <h4 className="font-bold text-xl text-gray-800">{allocation.category}</h4>
                              <p className="text-sm text-gray-600">{allocation.description}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-gray-800">
                                ${allocation.amount.toLocaleString()}
                              </div>
                              <div className="text-lg font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                                {((allocation.amount / parseFloat(totalBudget)) * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                          
                          <div className="w-full bg-white/60 rounded-full h-3 overflow-hidden shadow-inner">
                            <div 
                              className={`h-full bg-gradient-to-r ${categoryData[allocation.category]?.color || 'from-gray-400 to-gray-500'} rounded-full transition-all duration-1000 ease-out shadow-sm`}
                              style={{ 
                                width: `${(allocation.amount / parseFloat(totalBudget)) * 100}%`,
                                animationDelay: `${index * 200}ms`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
      />
    </div>
  );
};

export default BudgetAllocator;
